import {
  CalendarEventFormFields,
  CalendarEventRef,
  ExternalCalendarEventPreview,
} from '@griply/types';
import { CreateCalendarEventInitial } from '@griply/utils';
import React, { createContext } from 'react';
import { PopupMenu } from 'shared/components/ui/popup-menu';
import { ExternalEventDragRequest } from 'shared/lib/full-calendar';

// viewport coordinates the popup floats from — captured from the clicked event's rect
export type EventPopupAnchor = { top: number; left: number };

export type OpenEditEventOptions = {
  anchor: EventPopupAnchor;
  // everything the clicked grid event already knows, so the popup paints before the fetch
  seed: CalendarEventFormFields;
  // the event at the provider; absent on responses from an older backend
  url?: string;
  // what the list answered about rows the detail fetch would otherwise have to fill in
  preview?: ExternalCalendarEventPreview;
};

// anchoring is the caller's: the card floats from the cursor, the popup header from its trigger
export type ExternalEventMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'onOutside'
> & {
  eventTarget: CalendarEventRef;
  isRecurring: boolean;
  // absent on an older backend; only an explicit `false` marks you as an invitee
  isOrganizer?: boolean;
  // omitted where the event is already open — there is nothing to navigate to
  onEdit?: () => void;
  // duplication needs fields, and only the surface that has them knows where to get them
  onDuplicate?: () => void;
  onClose: () => void;
  // fired only when an action actually ran, so a host can close itself behind the menu
  onAfterAction?: () => void;
};

export type EventFormContextProps = {
  onCreateEvent: (
    initial: CreateCalendarEventInitial,
    anchor: EventPopupAnchor,
  ) => void;
  onEditEvent: (
    target: CalendarEventRef,
    options: OpenEditEventOptions,
  ) => void;
  // `revert` puts a deferred (recurring) drag back without waiting on a refetch
  onDragEvent: (request: ExternalEventDragRequest, revert: () => void) => void;
  // copies an event nothing has loaded yet — the surfaces that own fields duplicate from those
  onDuplicateEvent: (target: CalendarEventRef) => void;
  // the provider hands the connected menu down so ui hosts never import connected/ themselves
  ExternalEventMenu: React.ComponentType<ExternalEventMenuProps>;
};

export const EventFormContext = createContext<EventFormContextProps>({
  onCreateEvent: () => {},
  onEditEvent: () => {},
  onDragEvent: () => {},
  onDuplicateEvent: () => {},
  ExternalEventMenu: () => null,
});
